import { useState, useCallback } from "react";
import { RowConfig, GameState, GameStatus, GameUtilities } from "../types";
import { ethers } from "ethers";
import {
  generateAllDeathTiles,
  createCommitmentHash,
} from "../services/provablyFair";
import { soundService } from "../services/sound";

// Generate demo game seed with proper format
function generateDemoGameSeed(): string {
  // Generate 32 random bytes and convert to hex (64 characters)
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  const hexString = Array.from(array, (byte) =>
    byte.toString(16).padStart(2, "0"),
  ).join("");
  return `demo-${hexString}`;
}

// Generate death tiles for demo game using the same algorithm as real game
async function generateDemoDeathTiles(seed: string, rowConfigs: RowConfig[]) {
  return await generateAllDeathTiles(seed, rowConfigs);
}

// Generate commitment hash for demo game
async function generateDemoCommitmentHash(
  seed: string,
  rowConfigs: RowConfig[],
) {
  return await createCommitmentHash("v1", rowConfigs, seed);
}

export const useDemoGame = () => {
  const [demoGameState, setDemoGameState] = useState<GameState>({
    loading: false,
    wallet: { connected: false },
  });

  const startDemoGame = useCallback(async (rowConfigs: RowConfig[]) => {
    const seed = generateDemoGameSeed(); // Now generates "demo-" + 64-char hex string
    const gameId = `demo-${Date.now()}`;

    // Generate death tiles and commitment hash using the same algorithm as real game
    const deathTiles = await generateDemoDeathTiles(seed, rowConfigs);
    const commitmentHash = await generateDemoCommitmentHash(seed, rowConfigs);

    // Mock utilities as purchased for demo mode
    // Note: DXF Mining Boost is NOT mocked - users need to understand this requires real DXF tokens
    const demoUtilities: GameUtilities = {
      rowSkip: {
        purchased: true,
        // usedOnRow will be set when row skip is used
      },
      dxfMiningBoost: {
        purchased: false, // Not available in demo - requires real DXF tokens
        count: 0,
      },
      phoenixRevival: {
        purchased: true,
        // usedOnRow will be set when phoenix revival is used
      },
    };

    const demoGame = {
      gameId,
      gameSeedHash: commitmentHash,
      rowConfigs,
      status: "active" as const,
      currentRow: 0,
      moves: [],
      seed: seed,
      deathTiles,
      playerAddress: "demo-player",
      betAmount: ethers.parseEther("1").toString(),
      utilities: demoUtilities,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    setDemoGameState({
      loading: false,
      wallet: { connected: false },
      game: demoGame,
    });

    return demoGame;
  }, []);

  const makeDemoMove = useCallback((tileIndex: number) => {
    setDemoGameState((prev) => {
      if (!prev.game || prev.game.status !== "active") {
        return prev;
      }

      const currentRow = prev.game.currentRow;
      const deathTile = prev.game.deathTiles?.find(
        (dt) => dt.row === currentRow,
      );
      const isDeathTile = deathTile && deathTile.position === tileIndex;

      // Check if phoenix revival is available for death tiles
      const phoenixUtility = prev.game.utilities?.phoenixRevival;
      const canUsePhoenixRevival = phoenixUtility?.purchased && phoenixUtility.usedOnRow === undefined;

      let moveResult: "safe" | "death" | "revived" = isDeathTile ? "death" : "safe";
      
      // If death tile hit and phoenix revival available, use it
      if (isDeathTile && canUsePhoenixRevival) {
        moveResult = "revived";
        // Load and play phoenix sound
        soundService.loadPhoenixSound();
        soundService.playPhoenixSound();
      } else if (isDeathTile) {
        soundService.playDeathTile();
      } else {
        if (deathTile && Math.abs(deathTile?.position - tileIndex) <= 1) {
          soundService.playDangerTile();
        } else {
          soundService.playGoodTile();
        }
      }

      const newMove = {
        row: currentRow,
        tileIndex,
        timestamp: new Date().toISOString(),
        result: moveResult,
      };

      const newMoves = [...prev.game.moves, newMove];
      let newStatus: GameStatus = prev.game.status;
      let newCurrentRow = prev.game.currentRow;
      let updatedUtilities = prev.game.utilities;

      if (isDeathTile && canUsePhoenixRevival) {
        // Phoenix Revival: continue game from next row
        if (currentRow === prev.game.rowConfigs.length - 1) {
          // If on last row, win the game
          newStatus = "won";
        } else {
          newCurrentRow = currentRow + 1;
        }
        
        // Mark phoenix revival as used
        updatedUtilities = {
          ...prev.game.utilities,
          phoenixRevival: {
            ...phoenixUtility,
            usedOnRow: currentRow,
          },
        };
      } else if (isDeathTile) {
        // Regular death without revival
        newStatus = "lost";
      } else {
        // Safe tile
        if (currentRow === prev.game.rowConfigs.length - 1) {
          newStatus = "won";
        } else {
          newCurrentRow = currentRow + 1;
        }
      }

      const updatedGame = {
        ...prev.game,
        moves: newMoves,
        status: newStatus,
        currentRow: newCurrentRow,
        utilities: updatedUtilities,
        updatedAt: new Date().toISOString(),
      };

      return {
        ...prev,
        game: updatedGame,
      };
    });
  }, []);

  const resetDemoGame = useCallback(() => {
    setDemoGameState({
      loading: false,
      wallet: { connected: false },
    });
  }, []);

  const skipDemoRow = useCallback(() => {
    setDemoGameState((prev) => {
      if (!prev.game || prev.game.status !== "active") {
        return prev;
      }

      // Check if row skip utility is available
      const rowSkipUtility = prev.game.utilities?.rowSkip;
      if (!rowSkipUtility?.purchased || rowSkipUtility.usedOnRow !== undefined) {
        return prev;
      }

      const currentRow = prev.game.currentRow;

      // Create a "skipped" move for this row
      const skipMove = {
        row: currentRow,
        tileIndex: -1, // Special marker for skipped rows
        timestamp: new Date().toISOString(),
        result: "skipped" as const,
      };

      const newMoves = [...prev.game.moves, skipMove];
      let newStatus: GameStatus = prev.game.status;
      let newCurrentRow = prev.game.currentRow;

      // Check if this was the last row
      if (currentRow === prev.game.rowConfigs.length - 1) {
        newStatus = "won";
      } else {
        newCurrentRow = currentRow + 1;
      }

      // Update utilities to mark row skip as used
      const updatedUtilities = {
        ...prev.game.utilities,
        rowSkip: {
          ...rowSkipUtility,
          usedOnRow: currentRow,
        },
      };

      const updatedGame = {
        ...prev.game,
        moves: newMoves,
        status: newStatus,
        currentRow: newCurrentRow,
        utilities: updatedUtilities,
        updatedAt: new Date().toISOString(),
      };

      return {
        ...prev,
        game: updatedGame,
      };
    });
  }, []);

  const cashOutDemo = useCallback(() => {
    setDemoGameState((prev) => {
      if (!prev.game) return prev;

      return {
        ...prev,
        game: {
          ...prev.game,
          status: "cashed_out",
          updatedAt: new Date().toISOString(),
        },
      };
    });
  }, []);

  return {
    demoGameState,
    startDemoGame,
    makeDemoMove,
    skipDemoRow,
    resetDemoGame,
    cashOutDemo,
  };
};
